import { ApiResponse } from "@/services/api-service";
import {
  getAccessToken,
  getRefreshToken,
  useAuthStore,
} from "@/store/auth-store";
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";
import { toast } from "sonner";

// Define the token response type
export interface TokenPayload {
  accessToken: string;
  refreshToken: string;
}

const baseURL = import.meta.env.VITE_API_ENDPOINT;

// Create axios instance
export const axiosInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
// Store for callbacks to be executed after token refresh
let refreshSubscribers: ((token: string) => void)[] = [];

// Function to add callback to subscribers
const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Function to execute subscribers with new token
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// Function to refresh token
const refreshAuthToken = async (): Promise<string> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  try {
    const response = await axios.post<ApiResponse<TokenPayload>>(
      `${baseURL}/auth/refresh-token`,
      {
        refreshToken,
      },
    );

    const newTokens = response.data.payload;
    // Store new tokens in Zustand
    useAuthStore
      .getState()
      .setTokens(newTokens, useAuthStore.getState().rememberMe);

    return newTokens.accessToken;
  } catch (error) {
    // If refresh token is invalid, clear tokens and redirect to login
    useAuthStore.getState().logout();
    window.location.href = "/login?session=expired";
    throw error;
  }
};

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor to handle token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // If error is 401 and we haven't retried yet
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      getRefreshToken() &&
      !originalRequest.url?.includes("/auth/login")
    ) {
      if (isRefreshing) {
        // If already refreshing, wait for new token
        return new Promise<AxiosResponse>((resolve) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      // Set refreshing flag
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Get new token

        const newToken = await refreshAuthToken();
        // Notify subscribers
        onTokenRefreshed(newToken);
        // Reset flag
        isRefreshing = false;
        // Retry original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        // If refresh fails, redirect to login
        useAuthStore.getState().logout();
        toast.error("Session expired", {
          description: "Please log in again.",
        });
        if (typeof window !== "undefined") {
          window.location.href = "/login?session=expired";
        }
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors
    const errorMessage =
      (error.response?.data as { message?: string })?.message ||
      error.message ||
      "An unexpected error occurred";

    // Show toast for API errors
    if (error.response && error.response.status !== 401) {
      toast.error(`Error ${error.response.status}`, {
        description: errorMessage,
      });
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
